/**
 * „Steht schon im Kundenstamm?" — die Kunden, die der Absender eines Leeds sein
 * könnten, jeweils mit dem Grund (Server: `leed_kandidaten_store`).
 *
 * Benjamin, 26.10.2026: Frau Rudeck stand mit einem Tippfehler in der Mailadresse
 * im Stamm. Das Nachrichtenfenster meldete „noch kein Kunde", „Als Kunde
 * übernehmen" legte sie ein zweites Mal an, und ihr Pflegeprojekt war an der
 * Nachricht nicht wählbar. Jetzt steht der Treffer oben, bevor jemand anlegt.
 *
 * Ein Tipp ordnet zu. Weicht die Mailadresse ab, fragt der Kasten vorher, ob die
 * neue die alte ersetzt oder dazukommt — mal ist die alte ein Tippfehler, mal
 * schreibt der Kunde von einem Zweitkonto. Hat der Kunde gar keine Mail, wird
 * sie ohne Frage eingetragen.
 */
import { useState } from "react";

import type { LeedKandidat } from "../../hooks/usePost";

export type MailWahl = "" | "ersetzen" | "dazu";

type Props = {
  kandidaten: LeedKandidat[];
  /** Die Absenderadresse des Leeds. */
  leedMail: string;
  disabled?: boolean;
  onZuordnen: (k: LeedKandidat, mail: MailWahl) => void;
};

const GRUND: Record<string, string> = {
  "Mailadresse": "gleiche Mailadresse",
  "ähnliche Mailadresse": "fast gleiche Mailadresse",
  "Telefon": "gleiche Telefonnummer",
  "Name": "gleicher Name",
  "Anschrift": "gleiche Anschrift",
};

/** „Kundin" bei Frau, sonst „Kunde". */
export function kundeWort(anrede: string): string {
  return anrede === "frau" ? "Kundin" : "Kunde";
}

export default function KundenKandidaten({ kandidaten, leedMail, disabled, onZuordnen }: Props) {
  const [frage, setFrage] = useState<LeedKandidat | null>(null);
  if (!kandidaten.length) return null;
  const hart = kandidaten.some((k) => k.hart);

  const waehlen = (k: LeedKandidat) => {
    if (leedMail && k.email && !k.mail_bekannt) setFrage(k);
    else onZuordnen(k, leedMail && !k.mail_bekannt ? "ersetzen" : "");
  };

  if (frage) {
    return (
      <div className="nb-bekannt" role="group" aria-label="Mailadresse übernehmen">
        <p className="nb-bekannt-titel">Welche Mailadresse gilt für {frage.name}?</p>
        <p className="nb-bekannt-text">
          Die Anfrage kam von <strong>{leedMail}</strong>, im Kundenstamm steht <strong>{frage.email}</strong>.
        </p>
        <div className="nb-bekannt-knoepfe">
          <button type="button" className="vg-btn vg-btn-primary" disabled={disabled} onClick={() => onZuordnen(frage, "ersetzen")}>
            Neue ersetzt die alte
          </button>
          <button type="button" className="vg-btn" disabled={disabled} onClick={() => onZuordnen(frage, "dazu")}>
            Als weitere Adresse dazu
          </button>
          <button type="button" className="vg-btn vg-btn-ghost" disabled={disabled} onClick={() => setFrage(null)}>Zurück</button>
        </div>
      </div>
    );
  }

  return (
    <div className="nb-bekannt" role="group" aria-label="Schon im Kundenstamm">
      <p className="nb-bekannt-titel">
        {hart ? "Diese Mailadresse gehört schon zu:" : kandidaten.length === 1 ? "Steht vermutlich schon im Kundenstamm:" : "Steht vermutlich schon im Kundenstamm — einer davon?"}
      </p>
      {kandidaten.map((k) => (
        <div className="nb-kandidat" key={k.id}>
          <div className="nb-kandidat-text">
            <strong>{k.name}</strong>
            {(k.anschrift || k.email) && <small>{[k.anschrift, k.email].filter(Boolean).join(" · ")}</small>}
            <small>{k.gruende.map((g) => GRUND[g] || g).join(", ")}</small>
          </div>
          <button type="button" className="vg-btn vg-btn-primary" disabled={disabled} onClick={() => waehlen(k)}>
            {kundeWort(k.anrede)} zuordnen
          </button>
        </div>
      ))}
    </div>
  );
}
